"use client";

import Link from "next/link";
import posthog from "posthog-js";
import { useRef } from "react";
import { buttonClass } from "@/components/ui";
import {
  PREMIUM_BILLING_NOTE,
  PREMIUM_PRICE_LABEL,
} from "@/lib/portfolio/entitlements";

/**
 * The portfolio pitch popup — a passport-frame modal that sells attaching a
 * changemaker page to job applications. Wraps any clickable `children` (a video
 * thumbnail, a demo poster) as the trigger and renders the dialog itself.
 *
 * Native <dialog>: Escape and backdrop-click close for free, focus is trapped
 * by the platform, no portal dependency. Styled via .pitch-dialog in globals.css.
 *
 * `showLiveExample` controls the "See a live example →" link: shown on marketing
 * surfaces (the homepage), hidden on the live example itself (the demo page),
 * where linking there would just be circular.
 */
export function PortfolioPitch({
  children,
  surface = "unknown",
  showLiveExample = true,
  className = "",
  ariaLabel = "Why attach a changemaker portfolio to your applications",
}: {
  children: React.ReactNode;
  /** Where this pitch lives — attached to analytics events. */
  surface?: string;
  /** Show the "See a live example →" link (homepage yes, demo page no). */
  showLiveExample?: boolean;
  /** Classes for the trigger button (layout/appearance of the clickable area). */
  className?: string;
  ariaLabel?: string;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const open = () => {
    dialogRef.current?.showModal();
    if (posthog.__loaded)
      posthog.capture("portfolio_pitch_opened", { surface });
  };
  const close = () => dialogRef.current?.close();
  const captureCta = (cta: string) => {
    if (posthog.__loaded)
      posthog.capture("portfolio_pitch_cta_clicked", { surface, cta });
  };

  return (
    <>
      <button
        type="button"
        onClick={open}
        aria-haspopup="dialog"
        aria-label={ariaLabel}
        className={`cursor-pointer text-left ${className}`}
      >
        {children}
      </button>

      <dialog
        ref={dialogRef}
        className="pitch-dialog"
        aria-labelledby="pitch-title"
        onClick={(e) => {
          // Backdrop click: the dialog element itself is the click target
          // only when the click lands outside the inner panel.
          if (e.target === dialogRef.current) close();
        }}
      >
        <div className="overflow-hidden rounded-xl border-2 border-ink bg-white">
          <div className="relative">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/images/video-intro-thumb.jpg"
              alt=""
              className="h-36 w-full object-cover sm:h-40"
            />
            <span aria-hidden className="absolute inset-0 bg-ink/15" />
            <span className="stamp stamp-tilt absolute left-4 top-4 bg-white/95 text-brand-700">
              Seen by panels
            </span>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-lg leading-none text-ink shadow-sm transition-colors hover:bg-white"
            >
              ×
            </button>
          </div>

          <div className="p-6 sm:p-7">
            <p className="telex flex items-center gap-2 text-[9.5px] text-brand-700">
              <span aria-hidden className="inline-block h-[6px] w-[6px] bg-current" />
              Your portfolio
            </p>
            <h2
              id="pitch-title"
              className="display-calm mt-3 text-2xl font-semibold leading-tight text-ink"
            >
              Give the panel a face to remember.
            </h2>
            <p className="mt-3 text-[14.5px] leading-relaxed text-neutral-600">
              On paper, hundreds of applications look identical. A changemaker
              portfolio attaches the person — projects with outcomes, a
              service record, and a 90-second video introduction — one link in
              every application, built to move you from the pile to the
              interview.
            </p>

            <ol className="mt-5 space-y-2.5">
              {[
                ["01", "Drop your CV — your page drafts itself in minutes."],
                ["02", "Record a 90-second introduction in the browser."],
                ["03", "Put one link in every cover letter and PHP."],
              ].map(([n, t]) => (
                <li key={n} className="flex gap-3">
                  <span className="telex mt-0.5 shrink-0 text-brand-600">
                    {n}
                  </span>
                  <span className="text-[13.5px] leading-relaxed text-neutral-700">
                    {t}
                  </span>
                </li>
              ))}
            </ol>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                href="/dashboard"
                onClick={() => captureCta("create")}
                className={buttonClass("primary", "sm")}
              >
                Create your portfolio
              </Link>
              {showLiveExample ? (
                <Link
                  href="/p/amina-torres"
                  onClick={() => captureCta("see_example")}
                  className="text-[13px] font-semibold text-brand-700 hover:text-brand-800"
                >
                  See a live example →
                </Link>
              ) : null}
            </div>
            <p className="telex mt-4 text-[8.5px] text-neutral-400">
              {PREMIUM_PRICE_LABEL} · {PREMIUM_BILLING_NOTE} · cancel anytime
            </p>
          </div>
        </div>
      </dialog>
    </>
  );
}
